import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { groupEpisodes, seriesProgress, isEpisodeCard } from '../lib/longformGroups';

// ── Status config ─────────────────────────────────────────────────────────────

const PIPELINE_STEPS = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted'];

const LIVE_TRANSITIONS = {
  Scripted:   ['Filming', 'Incomplete', 'Archived'],
  Filming:    ['Filmed',  'Incomplete', 'Archived'],
  Filmed:     ['Editing', 'Incomplete', 'Archived'],
  Editing:    ['Qual',    'Incomplete', 'Archived'],
  Qual:       ['Approved', 'Editing'],
  Approved:   ['Posted'],
  Posted:     ['Approved'],
  Incomplete: ['Filming', 'Archived'],
  Archived:   ['Filming', 'Scripted'],
};

const PILL_CLASS = {
  Scripted:   'pill-scripted',
  Filming:    'pill-filming',
  Filmed:     'pill-filmed',
  Editing:    'pill-editing',
  Qual:       'pill-qual',
  Approved:   'pill-approved',
  Posted:     'pill-posted',
  Incomplete: 'pill-incomplete',
  Archived:   'pill-archived',
};

const ALL_STATUSES = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted', 'Incomplete', 'Archived'];
const CHIP_STATUSES = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Incomplete', 'Posted', 'Archived'];

function formatDate(str) {
  if (!str) return '—';
  // Parse date-only strings as local date to avoid UTC offset shifting the day
  const [y, m, d] = str.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function toInputDate(str) {
  if (!str) return '';
  return typeof str === 'string' ? str.slice(0, 10) : new Date(str).toISOString().slice(0, 10);
}

// ── LiveProduction page ───────────────────────────────────────────────────────

export default function LiveProduction() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [records,       setRecords]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [search,        setSearch]        = useState(() => searchParams.get('q') || '');
  const [statusFilter,  setStatusFilter]  = useState(() => searchParams.get('status') || '');
  // Posted view only: include cards that were filed away with Archive.
  const [showArchived,  setShowArchived]  = useState(false);
  const [clientFilter,  setClientFilter]  = useState(() => searchParams.get('client') || '');
  const [editorFilter,  setEditorFilter]  = useState(() => searchParams.get('editor') || '');
  const [clients,       setClients]       = useState([]);
  const [members,       setMembers]       = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showRiffModal,  setShowRiffModal]  = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [ctxMenu,        setCtxMenu]        = useState(null); // { x, y, record }

  // Resizable columns — 8 data columns (Run 4: Frame Links + File removed,
  // Actor renamed Talent and moved between Date and Notes).
  // ⚠️ Storage key bumped _v2: widths persist as a JSON array keyed by column
  // INDEX, and every user has a 10-wide payload saved under 'colWidths_live'.
  // A stale payload against the new 8-column set would misalign the whole grid,
  // so the old key is abandoned and widths rebuild from defaults once. (The
  // hook's length check would also discard a 10-wide array, but the versioned
  // key makes the reset explicit and survives future same-length reorderings.)
  const { template: liveColTemplate, startDrag: startLiveColDrag } = useResizableColumns(
    'colWidths_live_v2',
    [250, 130, 130, 110, 70, 90, 190, 200]
  );
  // Whole-column expand for Talent (D-021 precedent — the Characters column):
  // collapsed (default) every cell is a one-line scrolling strip; expanded,
  // every row wraps to show all of its talent. One flag, not one per row.
  const [talentExpanded, setTalentExpanded] = useState(false);

  // Bulk selection (owner order 2026-09-01, mirrors the Scripts list): tick
  // boxes per row + mass status change. Each row runs through the SAME
  // validated status pipeline as the card buttons (side effects intact —
  // posted dates, footage archive, script sync, notifications); rows where
  // the jump is not a legal transition, or the caller lacks the role, are
  // skipped and counted rather than force-changed.
  const canSelectRows = !user?.isExternal;
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus,  setBulkStatus]  = useState('');
  const [bulkBusy,    setBulkBusy]    = useState(false);
  // A fixed checkbox cell is PREPENDED to the template — the resizable width
  // array stays 8-wide and index-aligned, so no storage-key bump (Run 4 §).
  const gridColsWithCheck = canSelectRows ? `28px ${liveColTemplate}` : liveColTemplate;

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleBulkStatus() {
    const ids = [...selectedIds];
    if (!bulkStatus || ids.length === 0 || bulkBusy) return;
    if (!window.confirm(`Set ${ids.length} production${ids.length !== 1 ? 's' : ''} to "${bulkStatus}"? This jumps straight to that status — no pipeline steps required.`)) return;
    setBulkBusy(true);
    let changed = 0;
    const skipped = [];
    for (const id of ids) {
      try {
        // force: bulk is a direct set (owner call 2026-09-01) — any status,
        // no transition walking. Role gates still apply per row.
        await api.patch(`/api/live-production/${id}/status`, { status: bulkStatus, changedBy: user.id, force: true });
        changed++;
      } catch (e) {
        const rec = records.find(r => r.id === id);
        const name = rec?.script?.scriptName || rec?.notes || id;
        skipped.push(`${name} — ${e.response?.data?.error || 'failed'}`);
      }
    }
    setBulkBusy(false);
    setSelectedIds(new Set());
    setBulkStatus('');
    fetchRecords();
    alert(
      `${changed} of ${ids.length} card${ids.length !== 1 ? 's' : ''} set to ${bulkStatus}.`
      + (skipped.length ? `\n\nSkipped ${skipped.length}:\n${skipped.slice(0, 10).join('\n')}${skipped.length > 10 ? `\n…and ${skipped.length - 10} more` : ''}` : '')
    );
  }

  // Rows that leave the view (filter change, mass-archive, refetch) leave the
  // selection too — a hidden row must never be silently mass-changed.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(records.map(r => r.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [records]);
  // Inline notes editor state: { id, value } | null
  const [notesEdit, setNotesEdit] = useState(null);

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      // D-057: the client filter is applied CLIENT-SIDE so its options can be
      // derived from the rows actually present in the view — the grid loads its
      // whole list anyway, and this is what lets an inactive client with real
      // rows stay pickable while a zero-row stub disappears.
      if (editorFilter) params.set('editorId', editorFilter);
      if (statusFilter === 'Posted') {
        // Explicit archive view — show only Posted. Archived (filed) cards
        // stay hidden unless the "Show archived" toggle is on.
        params.set('status', 'Posted');
        if (showArchived) params.set('includeArchived', 'true');
      } else if (statusFilter === 'ALL') {
        // P-7(b): every status including Posted (archived stay behind the toggle).
        if (showArchived) params.set('includeArchived', 'true');
      } else if (statusFilter) {
        params.set('status', statusFilter);
      } else {
        // Default: hide Posted (finished) and Archived (shelved) records —
        // both have explicit status views.
        params.set('excludePosted', 'true');
      }
      const res = await api.get(`/api/live-production?${params}`);
      setRecords(res.data);
    } catch {
      setError('Failed to load records.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, editorFilter, showArchived]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Restore the open card from ?card= on first load (refresh / shared link /
  // "open in new tab"). Runs once, after records arrive — deliberately NOT on
  // every URL change. Closing a card clears ?card=, and a URL-driven reopen
  // effect would race that removal and immediately reopen the card (it could
  // never be dismissed). Keeping this one-shot avoids that loop.
  // P-1 (Context.HeliconflowProductionPage.md): /live?card=<id> is now a REDIRECT
  // to the Production page, so every stored deep link, notification and
  // "Copy Link" from the modal era still lands on the card — as a page.
  useEffect(() => {
    const cardId = searchParams.get('card');
    if (!cardId) return;
    const p = new URLSearchParams(searchParams); p.delete('card');
    const rest = p.toString();
    navigate(`/production/${cardId}${rest ? `?${rest}` : ''}`, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Explicit deep-link via navigation state (notification, Edit Queue) — honored
  // on each such navigation, then the transient state is consumed. The ?card=
  // mirror below keeps the URL shareable/refreshable. This depends on
  // location.state (not the whole location), so clearing ?card= on close can't
  // retrigger it.
  useEffect(() => {
    const openId = location.state?.openRecordId;
    if (!openId) return;
    navigate(`/production/${openId}`, { replace: true });
  }, [location.state, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mirror the open card into the URL so a refresh restores it. Skip the initial
  // mount run so we don't strip ?card= before the deep-link effect above opens it.
  const cardUrlSyncInit = useRef(false);
  useEffect(() => {
    if (!cardUrlSyncInit.current) { cardUrlSyncInit.current = true; return; }
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (selectedRecord?.id) p.set('card', selectedRecord.id); else p.delete('card');
      return p;
    }, { replace: true });
  }, [selectedRecord?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data)).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // ── Derived ──────────────────────────────────────────────────────────────────

  const statusCounts = useMemo(() => {
    const c = {};
    records.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [records]);

  // D-057: client filter options = clients with ≥1 row in the current view.
  const clientOptions = useMemo(() => {
    const seen = new Map();
    for (const r of records) {
      if (r.client && !seen.has(r.client.id)) seen.set(r.client.id, r.client);
    }
    // Keep the current selection pickable even if the view empties for it.
    if (clientFilter && !seen.has(clientFilter)) {
      const known = clients.find(c => c.id === clientFilter);
      if (known) seen.set(known.id, known);
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [records, clientFilter, clients]);

  const filteredRecords = useMemo(() => {
    let out = records;
    if (clientFilter) out = out.filter(r => r.client?.id === clientFilter);
    if (!search) return out;
    const q = search.toLowerCase();
    // §4.3: search must follow the rename — it matches the RESOLVED talent
    // names the Talent column shows, plus everything it matched before.
    // file_name stays searchable even though its column is gone (S7).
    return out.filter(r =>
      r.script?.scriptName?.toLowerCase().includes(q) ||
      // D-120: an episode matches on its own title / "Ep N" too, so the series surfaces when ANY episode matches.
      r.episodeTitle?.toLowerCase().includes(q) || (r.episodeNumber != null && `ep ${r.episodeNumber}`.includes(q)) ||
      r.actorName?.toLowerCase().includes(q) ||
      (r.resolvedTalent?.talents || []).some(t => t.name?.toLowerCase().includes(q)) ||
      r.notes?.toLowerCase().includes(q) ||
      r.fileName?.toLowerCase().includes(q) ||
      r.client?.name?.toLowerCase().includes(q)
    );
  }, [records, search, clientFilter]);

  const [liveSort, setLiveSort] = useState(() => ({ col: searchParams.get('sortBy') || '', dir: searchParams.get('sortDir') || 'asc' }));

  // Sync sort + filter state to URL params so browser back restores them.
  // Not while a /live?card=<id> redirect is in flight: this effect and the
  // redirect run in the same mount tick, and the later navigation wins.
  useEffect(() => {
    if (searchParams.get('card')) return;
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (search) p.set('q', search); else p.delete('q');
      if (statusFilter) p.set('status', statusFilter); else p.delete('status');
      if (clientFilter) p.set('client', clientFilter); else p.delete('client');
      if (editorFilter) p.set('editor', editorFilter); else p.delete('editor');
      if (liveSort.col) p.set('sortBy', liveSort.col); else p.delete('sortBy');
      if (liveSort.dir !== 'asc') p.set('sortDir', liveSort.dir); else p.delete('sortDir');
      return p;
    }, { replace: true });
  }, [liveSort, search, statusFilter, clientFilter, editorFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── D-120 Longform hierarchy: ONE series row per Longform script, episodes nested ──
  // Grouping runs AFTER filter + sort (below), so a text filter matches the series when ANY
  // episode matches and the expansion shows exactly which. Progress is computed from ALL the
  // episodes the grid loaded for that series (not the filtered subset), so "3/8 Posted" is true.
  // Explicit per-series override (Map key → bool) over the default: collapsed, or expanded while a
  // text search is active (so the expansion shows WHICH episodes matched). The toggle always works.
  const [expandedSeries, setExpandedSeries] = useState(() => new Map());
  const isSeriesExpanded = (key) => expandedSeries.has(key) ? expandedSeries.get(key) : !!search;
  const toggleSeries = (key) => setExpandedSeries(prev => { const n = new Map(prev); n.set(key, !isSeriesExpanded(key)); return n; });
  useEffect(() => { setExpandedSeries(new Map()); }, [search]);
  const allEpisodesBySeries = useMemo(() => { const m = new Map(); for (const r of records) if (isEpisodeCard(r)) { const k = r.scriptId || r.script?.id; if (!m.has(k)) m.set(k, []); m.get(k).push(r); } return m; }, [records]);
  const sortedRecords = useMemo(() => {
    if (!liveSort.col) return filteredRecords;
    const { col, dir } = liveSort;
    return [...filteredRecords].sort((a, b) => {
      let av = '', bv = '';
      // D-058: Talent is not sortable (multi-valued and derived); the old
      // Actor sort went with the rename.
      if      (col === 'Script / Title') { av = a.script?.scriptName || a.notes || ''; bv = b.script?.scriptName || b.notes || ''; }
      else if (col === 'Project')         { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (col === 'Producer')       { av = a.shootLead?.name || ''; bv = b.shootLead?.name || ''; }
      else if (col === 'Status')         { av = a.status || ''; bv = b.status || ''; }
      else if (col === 'Date')           { av = a.dateFilmed || a.shootScheduledDate || ''; bv = b.dateFilmed || b.shootScheduledDate || ''; }
      else return 0;
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filteredRecords, liveSort]);

  const incompleteRecords = useMemo(() => records.filter(r => r.status === 'Incomplete'), [records]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  async function handleRowStatusChange(record, newStatus, e) {
    e.stopPropagation();
    setOpenDropdownId(null);
    try {
      const res = await api.patch(`/api/live-production/${record.id}/status`, {
        status: newStatus, changedBy: user.id,
      });
      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, ...res.data } : r));
    } catch (err) {
      alert(err.response?.data?.error || 'Status update failed');
    }
  }

  function handleRecordUpdated(updated) {
    setRecords(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    setSelectedRecord(prev => prev?.id === updated.id ? { ...prev, ...updated } : prev);
  }

  // Inline notes save (§4.5) — same audited endpoint as the Script List's
  // Scheduled tab. On Riff rows this edits the card TITLE (notes IS the title).
  async function handleNotesSave(record) {
    if (!notesEdit || notesEdit.id !== record.id) return;
    const value = notesEdit.value;
    if (value === (record.notes || '')) { setNotesEdit(null); return; }
    try {
      await api.patch(`/api/tal/lp/${record.id}/notes`, { notes: value });
      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, notes: value } : r));
      setNotesEdit(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed.');
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Live Production</div>
          <div className="page-sub">
            {records.length} production{records.length !== 1 ? 's' : ''} · {incompleteRecords.length} incomplete
          </div>
        </div>
        <div className="topbar-actions">
          <button className="btn-ghost" onClick={fetchRecords}>↺ Refresh</button>
          <button className="btn-primary" onClick={() => setShowRiffModal(true)}>+ Riff Card</button>
        </div>
      </div>

      <div className="body" style={{ overflow: 'hidden', padding: '20px 36px 28px', gap: 14 }}>

        {/* Incomplete alert banner */}
        {incompleteRecords.length > 0 && (
          <div className="alert-banner" style={{ flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#d45555', fontSize: 16 }}>⚠</span>
              <span className="alert-text">
                {incompleteRecords.length} record{incompleteRecords.length !== 1 ? 's' : ''} need a reshoot scheduled
                {' — '}
                {incompleteRecords.slice(0, 3).map(r => r.script?.scriptName || '(Riff)').join(', ')}
                {incompleteRecords.length > 3 ? ` +${incompleteRecords.length - 3} more` : ''}
              </span>
            </div>
            <button className="btn-danger" onClick={() => setStatusFilter('Incomplete')}>
              Show Incomplete
            </button>
          </div>
        )}

        {/* Status chips */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
          {/* T-1 (Context.HeliconflowProdPageTouchups1.md): "All" pill — every status
              including Posted. Same filter state as the dropdown's All. */}
          <div
            data-testid="chip-all"
            className={`chip chip-all${statusFilter && statusFilter !== 'ALL' ? ' dimmed' : ''}${statusFilter === 'ALL' ? ' active' : ''}`}
            onClick={() => setStatusFilter(prev => prev === 'ALL' ? '' : 'ALL')}
            title="Show every status, including Posted"
          >
            <span>All</span>
          </div>
          {CHIP_STATUSES.map(s => (
            <div
              key={s}
              className={`chip chip-${s.toLowerCase()}${statusFilter && statusFilter !== 'ALL' && statusFilter !== s ? ' dimmed' : ''}`}
              onClick={() => setStatusFilter(prev => prev === s ? '' : s)}
            >
              <span>{s}</span>
              {statusCounts[s] != null && <span className="chip-count">{statusCounts[s]}</span>}
            </div>
          ))}
          {statusFilter && (
            <button
              className="btn-ghost"
              style={{ padding: '4px 10px', fontSize: 11 }}
              onClick={() => setStatusFilter('')}
            >
              Clear ×
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <input
            className="filter-input"
            style={{ flex: 1, maxWidth: 280 }}
            placeholder="Search scripts, talent…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="filter-input"
            style={{ width: 155 }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">Active (hides Posted)</option>
            <option value="ALL">All — including Posted</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(statusFilter === 'Posted' || statusFilter === 'ALL') && (
            <label
              style={{ fontSize: 11, color: 'var(--tx-muted)', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', whiteSpace: 'nowrap' }}
              title="Include Posted cards that were filed away with Archive"
            >
              <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
              Show archived
            </label>
          )}
          <select
            className="filter-input"
            style={{ width: 160 }}
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            title="Projects with at least one row in the current view"
          >
            <option value="">All projects</option>
            {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            className="filter-input"
            style={{ width: 155 }}
            value={editorFilter}
            onChange={e => setEditorFilter(e.target.value)}
          >
            <option value="">All editors</option>
            {members
              .filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User'))
              .map(m => <option key={m.id} value={m.id}>{m.name}</option>)
            }
          </select>
        </div>

        {/* Bulk status bar — appears while any rows are ticked. Each row goes
            through the validated status pipeline; illegal jumps are skipped
            and reported, never forced. */}
        {canSelectRows && selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: 'var(--violet)', fontWeight: 600 }}>
              {selectedIds.size} selected
            </span>
            <select
              className="filter-input"
              style={{ width: 170 }}
              value={bulkStatus}
              onChange={e => setBulkStatus(e.target.value)}
              title="Jumps straight to the chosen status — no pipeline steps. Only role limits (e.g. Approve) can skip a card."
            >
              <option value="">Set status to…</option>
              {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              className="btn-primary"
              style={{ padding: '6px 16px', fontSize: 12 }}
              onClick={handleBulkStatus}
              disabled={!bulkStatus || bulkBusy}
            >
              {bulkBusy ? 'Applying…' : `Apply to ${selectedIds.size}`}
            </button>
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}>
              Clear selection
            </button>
          </div>
        )}

        {/* Table card — D-022: ONE scroll container for header and rows, on
            both axes, with a sticky header. The columns are fixed pixel widths
            that can exceed the card on a laptop, and .card is overflow:hidden —
            adding a column to the old two-sibling layout is exactly what
            produced DEF-090 on the Scripts list. */}
        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ width: 'max-content', minWidth: '100%' }}>

          <div className="lp-table-head" style={{ gridTemplateColumns: gridColsWithCheck, position: 'sticky', top: 0, zIndex: 3, background: 'var(--bg-card2)' }}>
            {canSelectRows && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  title="Select all visible rows"
                  checked={sortedRecords.length > 0 && sortedRecords.every(r => selectedIds.has(r.id))}
                  onChange={e => {
                    if (e.target.checked) setSelectedIds(new Set(sortedRecords.map(r => r.id)));
                    else setSelectedIds(new Set());
                  }}
                  style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
                />
              </div>
            )}
            {['Script / Title', 'Project', 'Producer', 'Status', 'Thumb', 'Date', 'Talent', 'Notes'].map((h, i) => {
              // D-058: Talent is not sortable (multi-valued, derived); the old
              // Actor sort went with the rename. Remaining sorts: Script/Title,
              // Client, Producer, Status, Date.
              const sortable = ['Script / Title', 'Project', 'Producer', 'Status', 'Date'].includes(h);
              const active = liveSort.col === h;
              return (
                <div
                  key={h}
                  className="lp-th"
                  style={{ position: 'relative', ...(sortable ? { cursor: 'pointer', userSelect: 'none' } : {}) }}
                  onClick={sortable ? () => setLiveSort(prev => ({ col: h, dir: prev.col === h && prev.dir === 'asc' ? 'desc' : 'asc' })) : undefined}
                >
                  {h}
                  {sortable && (
                    <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.25, color: active ? 'var(--violet)' : 'inherit' }}>
                      {active && liveSort.dir === 'desc' ? '▼' : '▲'}
                    </span>
                  )}
                  {h === 'Talent' && (
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); setTalentExpanded(v => !v); }}
                      title={talentExpanded
                        ? 'Collapse the Talent column — back to one scrolling line per row'
                        : 'Expand the Talent column — every row wraps to show all of its talent'}
                      aria-label={talentExpanded ? 'Collapse the Talent column' : 'Expand the Talent column'}
                      aria-expanded={talentExpanded}
                      style={{
                        marginLeft: 6, flexShrink: 0, background: 'none', cursor: 'pointer',
                        border: '1px solid transparent', borderRadius: 4, padding: '1px 4px',
                        fontSize: 10, lineHeight: 1.2, fontFamily: 'var(--font)',
                        color: talentExpanded ? 'var(--violet)' : 'var(--tx-muted)',
                      }}
                    >
                      {talentExpanded ? '▴' : '▾'}
                    </button>
                  )}
                  <div
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 8, cursor: 'col-resize', zIndex: 1 }}
                    onMouseDown={e => startLiveColDrag(i, e)}
                  />
                </div>
              );
            })}
          </div>

          <div>
            {error ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--s-incomplete-t)' }}>{error}</div>
            ) : loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>Loading…</div>
            ) : filteredRecords.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--tx-faint)' }}>No records found</div>
            ) : groupEpisodes(sortedRecords).flatMap(entry => {
              if (entry.kind === 'series') {
                const all = allEpisodesBySeries.get(entry.scriptId) || entry.episodes;
                const progress = seriesProgress(all);
                const expanded = isSeriesExpanded(entry.key);
                const rows = [(
                  <SeriesRow key={entry.key} entry={entry} progress={progress} shownCount={entry.episodes.length} totalCount={all.length}
                    expanded={expanded} onToggle={() => toggleSeries(entry.key)} gridCols={gridColsWithCheck} selectable={canSelectRows}
                    checked={entry.episodes.every(e => selectedIds.has(e.id))}
                    onToggleSelect={() => setSelectedIds(prev => { const n = new Set(prev); const allSel = entry.episodes.every(e => n.has(e.id)); entry.episodes.forEach(e => allSel ? n.delete(e.id) : n.add(e.id)); return n; })} />
                )];
                if (expanded) for (const r of entry.episodes) rows.push(
                  <GridRow key={r.id} nested record={r} isDropdownOpen={openDropdownId === r.id} onOpenDropdown={e => { e.stopPropagation(); setOpenDropdownId(r.id); }} onStatusChange={(status, e) => handleRowStatusChange(r, status, e)}
                    onClick={() => { if (openDropdownId) { setOpenDropdownId(null); return; } navigate(`/production/${r.id}`); }} onContextMenu={(e, rec) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, record: rec }); }}
                    selectable={canSelectRows} checked={selectedIds.has(r.id)} onToggleSelect={() => toggleSelect(r.id)} gridCols={gridColsWithCheck} talentExpanded={talentExpanded} canEditNotes={!user?.isExternal}
                    notesEdit={notesEdit?.id === r.id ? notesEdit : null} onNotesEditStart={() => setNotesEdit({ id: r.id, value: r.notes || '' })} onNotesEditChange={v => setNotesEdit(prev => (prev?.id === r.id ? { ...prev, value: v } : prev))} onNotesEditCancel={() => setNotesEdit(null)} onNotesEditSave={() => handleNotesSave(r)} />
                );
                return rows;
              }
              const r = entry.record;
              return (
              <GridRow
                key={r.id}
                record={r}
                isDropdownOpen={openDropdownId === r.id}
                onOpenDropdown={e => { e.stopPropagation(); setOpenDropdownId(r.id); }}
                onStatusChange={(status, e) => handleRowStatusChange(r, status, e)}
                onClick={() => { if (openDropdownId) { setOpenDropdownId(null); return; } navigate(`/production/${r.id}`); }}
                onContextMenu={(e, rec) => { e.preventDefault(); setCtxMenu({ x: e.clientX, y: e.clientY, record: rec }); }}
                selectable={canSelectRows}
                checked={selectedIds.has(r.id)}
                onToggleSelect={() => toggleSelect(r.id)}
                gridCols={gridColsWithCheck}
                talentExpanded={talentExpanded}
                canEditNotes={!user?.isExternal}
                notesEdit={notesEdit?.id === r.id ? notesEdit : null}
                onNotesEditStart={() => setNotesEdit({ id: r.id, value: r.notes || '' })}
                onNotesEditChange={v => setNotesEdit(prev => (prev?.id === r.id ? { ...prev, value: v } : prev))}
                onNotesEditCancel={() => setNotesEdit(null)}
                onNotesEditSave={() => handleNotesSave(r)}
              />
              );
            })}
          </div>

          </div>{/* /width:max-content */}
          </div>{/* /scroll container */}
        </div>
      </div>

      {/* Dropdown overlay — closes open dropdown when clicking elsewhere */}
      {openDropdownId && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 99 }}
          onClick={() => setOpenDropdownId(null)}
        />
      )}

      {/* Riff Card modal */}
      {showRiffModal && (
        <RiffModal
          clients={clients}
          members={members}
          onClose={() => setShowRiffModal(false)}
          onCreated={record => {
            setRecords(prev => [record, ...prev]);
            setShowRiffModal(false);
          }}
        />
      )}

      {/* Right-click context menu */}
      {ctxMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 299 }} onClick={() => setCtxMenu(null)} />
          <div
            style={{
              position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 300,
              background: 'var(--bg-card)', border: '1px solid var(--border-mid)',
              borderRadius: 8, padding: '4px 0', boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              minWidth: 160,
            }}
            onClick={() => setCtxMenu(null)}
          >
            <div
              style={{ padding: '8px 14px', fontSize: 13, color: 'var(--tx-primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,109,255,0.12)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => window.open(`${window.location.origin}/production/${ctxMenu.record.id}`, '_blank')}
            >
              Open in new tab ↗
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Grid Row ──────────────────────────────────────────────────────────────────

// D-120: the ONE top-level entry a Longform series gets on the grid. Click → expand / collapse its
// episode sub-rows (GridRow nested). Aggregate progress: "3/8 Posted · next Ep 4 — Edit".
function SeriesRow({ entry, progress, shownCount, totalCount, expanded, onToggle, gridCols, selectable, checked, onToggleSelect }) {
  const rep = entry.record;
  const talents = [...new Map(entry.episodes.flatMap(e => e.resolvedTalent?.talents || []).map(t => [t.id || `n:${t.name}`, t])).values()];
  return (
    <div className="lp-row lp-series-row" data-testid="lp-series-row" data-episodes={totalCount} data-expanded={expanded ? 'true' : 'false'} style={gridCols ? { gridTemplateColumns: gridCols } : undefined} onClick={onToggle} title={expanded ? 'Collapse the episodes' : 'Expand the episodes'}>
      {selectable && (
        <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={checked} onChange={onToggleSelect} title="Select every episode of this series" style={{ accentColor: 'var(--violet)', cursor: 'pointer' }} />
        </div>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="script-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button type="button" className="lp-series-toggle" data-testid="lp-series-toggle" aria-expanded={expanded} onClick={e => { e.stopPropagation(); onToggle(); }}>{expanded ? '▾' : '▸'}</button>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.scriptName}</span>
          <span className="format-badge lf" style={{ fontSize: 9 }}>LF</span>
        </div>
        <span data-testid="lp-series-progress" style={{ display: 'block', fontSize: 10, color: 'rgba(139,109,255,0.85)', fontWeight: 600, letterSpacing: '0.04em', marginTop: 2 }}>
          {progress.label}{shownCount !== totalCount ? ` · ${shownCount} of ${totalCount} match` : ` · ${totalCount} episode${totalCount === 1 ? '' : 's'}`}
        </span>
      </div>
      <div className="lp-cell">{entry.client?.name || '—'}</div>
      <div className="lp-cell">{rep?.shootLead?.name || '—'}</div>
      <div className="lp-cell" onClick={e => e.stopPropagation()}>
        {progress.next ? <span className={`pill ${progress.next.productionActive === false ? 'pill-planned' : (PILL_CLASS[progress.next.status] || '')}`} title={`Next: Ep ${progress.next.episodeNumber}`}>{progress.next.productionActive === false ? 'Planned' : progress.next.status}</span> : <span className="pill pill-posted">Posted</span>}
      </div>
      <div className="lp-cell">{rep?.videoThumbnailUrl ? <img src={rep.videoThumbnailUrl} alt="" style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4 }} /> : <div style={{ width: 56, height: 32, borderRadius: 4, background: 'var(--bg-elevated)' }} />}</div>
      <div className="lp-cell" style={{ fontSize: 11, color: 'var(--tx-muted)' }}>—</div>
      <div className="lp-cell" style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {talents.length ? talents.map(t => <span key={t.id || `n:${t.name}`} className="talent-chip" style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: 'var(--bg-hover)', border: '1px solid var(--border)' }}>{t.initials || t.name}</span>) : <span style={{ fontSize: 11, color: 'var(--tx-faint)' }}>—</span>}
      </div>
      <div className="lp-cell" style={{ fontSize: 11, color: 'var(--tx-faint)' }}>{expanded ? 'Episodes below' : 'Click to show episodes'}</div>
    </div>
  );
}

function GridRow({
  record, isDropdownOpen, onOpenDropdown, onStatusChange, onClick, onContextMenu, gridCols,
  talentExpanded, canEditNotes, notesEdit, onNotesEditStart, onNotesEditChange, onNotesEditCancel, onNotesEditSave,
  selectable, checked, onToggleSelect, nested = false,
}) {
  const isIncomplete = record.status === 'Incomplete';
  const isRiff       = !record.scriptId;
  const displayName  = record.script?.scriptName || record.notes || '(Riff)';
  const episodeTag   = record.episodeNumber ? `Ep ${record.episodeNumber}${record.episodeTitle ? ` · ${record.episodeTitle}` : ''}` : '';
  const editorName   = record.editor?.name;
  const transitions  = LIVE_TRANSITIONS[record.status] || [];

  const dateDisplay = record.dateFilmed
    ? formatDate(record.dateFilmed)
    : record.shootScheduledDate
    ? `${formatDate(record.shootScheduledDate)}`
    : '—';

  return (
    <div
      className={`lp-row${isIncomplete ? ' incomplete' : ''}${nested ? ' nested' : ''}`}
      data-testid={nested ? 'lp-episode-row' : undefined}
      style={gridCols ? { gridTemplateColumns: gridCols } : undefined}
      onClick={onClick}
    >
      {selectable && (
        <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggleSelect}
            style={{ accentColor: 'var(--violet)', cursor: 'pointer' }}
          />
        </div>
      )}
      {/* Script / Title */}
      <div style={{ minWidth: 0 }} onContextMenu={onContextMenu ? e => onContextMenu(e, record) : undefined}>
        <div className="script-name">
          {displayName}
          {episodeTag && <span data-testid="episode-tag" style={{ display: 'block', fontSize: 10, color: 'rgba(139,109,255,0.85)', fontWeight: 600, letterSpacing: '0.04em', marginTop: 2 }}>{episodeTag}</span>}
          {record.isArchived && (
            <span
              title="Archived — filed away after Posted"
              style={{
                marginLeft: 6, padding: '1px 6px', borderRadius: 3,
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                color: 'var(--tx-muted)', fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
              }}
            >
              ARCHIVED
            </span>
          )}
          {record.isCooking && (
            <span
              title={record.cookingPhase === 1 ? 'Cooking — Phase 1 (Recipe shoot)' : 'Cooking — Phase 2 (Talking Head)'}
              style={{
                marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 2,
                padding: '1px 6px', borderRadius: 3,
                background: 'rgba(255,160,50,0.12)',
                border: '1px solid rgba(255,160,50,0.35)',
                color: 'rgba(255,160,50,1)',
                fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
              }}
            >
              🍳 P{record.cookingPhase || ''}
            </span>
          )}
        </div>
        {isRiff && <div className="riff-tag">RIFF</div>}
        {record.isCooking && record.cookingPhase === 2 && record.cookingPhase1?.script?.scriptName && (
          <div className="script-note" style={{ color: 'rgba(255,160,50,0.95)' }}>
            Phase 2 of {record.cookingPhase1.script.scriptName}
          </div>
        )}
        {!isRiff && editorName && <div className="script-note">Editor: {editorName}</div>}
        {isIncomplete && record.notes && (
          <div className="script-note" style={{ color: 'var(--s-incomplete-t)' }}>
            {record.notes}
          </div>
        )}
      </div>

      {/* Client */}
      <div className="lp-cell">{record.client?.name || '—'}</div>

      {/* Producer (underlying column: shoot_lead_id) */}
      <div className={record.shootLead?.name ? 'lp-cell' : 'lp-cell-muted'}>
        {record.shootLead?.name || '—'}
      </div>

      {/* Status — stop propagation so row click doesn't also fire */}
      <div onClick={e => e.stopPropagation()}>
        {transitions.length > 0 ? (
          <div className="pill-select" onClick={onOpenDropdown}>
            <span className={`pill ${PILL_CLASS[record.status] || ''}`}>{record.status}</span>
            <span className="pill-chevron">▾</span>
          </div>
        ) : (
          <span className={`pill ${PILL_CLASS[record.status] || ''}`}>{record.status}</span>
        )}

        {isDropdownOpen && (
          <div className="status-dropdown">
            {transitions.map(t => (
              <div key={t} className="status-dropdown-item" onClick={e => onStatusChange(t, e)}>
                <span className={`pill ${PILL_CLASS[t] || ''}`}>{t}</span>
              </div>
            ))}
          </div>
        )}

        {isIncomplete && (
          <button className="reshoot-btn" onClick={e => { e.stopPropagation(); onClick(); }}>
            Schedule reshoot →
          </button>
        )}
      </div>

      {/* Frame Links and File columns removed (Run 4 §4.1). Per S7 the FIELDS
          survive: frame_link / frame_folder_link / file_name / file_location
          stay on the record, stay editable on the Video Card, and the Cooking
          Phase-2 banner still reads frame_link. Only the grid columns went. */}

      {/* Thumbnail */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {record.videoThumbnailUrl
          ? <img src={record.videoThumbnailUrl} alt="" style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
          : <div style={{ width: 56, height: 32, background: 'var(--bg-input)', borderRadius: 4 }} />
        }
      </div>

      {/* Date */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{dateDisplay}</div>
        {record.postedDate && (
          <div style={{ fontSize: 10, color: 'var(--s-approved-t)', marginTop: 2 }}>
            Posted {formatDate(record.postedDate)}
          </div>
        )}
      </div>

      {/* Talent (§4.2) — derived, multi-valued: booked solid · mapping default
          greyed · legacy talent_id solid · legacy actor_name marked
          unstructured · explicit "No talent" marker, never a blank cell.
          Collapsed: one scrolling line (no scrollbar — row height must not
          change); expanded (whole column, D-021): wraps and the row grows. */}
      <div
        style={{
          display: 'flex', gap: 4, minWidth: 0, alignItems: 'flex-start',
          ...(talentExpanded
            ? { flexWrap: 'wrap' }
            : { flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }),
        }}
      >
        {(() => {
          const rt = record.resolvedTalent || { kind: 'none', talents: [] };
          if (rt.kind === 'none' || rt.talents.length === 0) {
            return <span style={{ fontSize: 11, color: 'var(--tx-faint)', fontStyle: 'italic', flexShrink: 0 }}>No talent</span>;
          }
          const chipStyle = {
            booked:   { background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--tx-primary)' },
            legacy:   { background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--tx-primary)' },
            mapped:   { background: 'transparent', border: '1px solid var(--border)', color: 'var(--tx-muted)' },
            freeform: { background: 'transparent', border: '1px dashed var(--border)', color: 'var(--tx-muted)', fontStyle: 'italic' },
          }[rt.kind] || {};
          const kindTitle = {
            booked:   'Booked on a shoot segment',
            mapped:   'Mapping default (registry / per-script cast) — not yet booked',
            legacy:   'Assigned talent (card-level)',
            freeform: 'Unstructured legacy actor name',
          }[rt.kind] || '';
          return rt.talents.map((t, idx) => (
            <span
              key={t.id || `n-${idx}`}
              title={`${t.name}${kindTitle ? ` — ${kindTitle}` : ''}`}
              style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                padding: '2px 6px', borderRadius: 4, flexShrink: 0, maxWidth: '100%',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                ...chipStyle,
              }}
            >
              {t.name}
            </span>
          ));
        })()}
      </div>

      {/* Notes (§4.5) — inline editable, same audited endpoint as the Script
          List. ⚠️ Riff cards keep their TITLE here, so on Riff rows the editor
          edits the title and says so. */}
      <div onClick={e => e.stopPropagation()} style={{ minWidth: 0 }}>
        {notesEdit ? (
          <input
            autoFocus
            className="form-input"
            style={{ fontSize: 11, padding: '4px 8px', width: '100%' }}
            value={notesEdit.value}
            onChange={e => onNotesEditChange(e.target.value)}
            onBlur={onNotesEditSave}
            onKeyDown={e => {
              if (e.key === 'Enter') onNotesEditSave();
              if (e.key === 'Escape') onNotesEditCancel();
            }}
          />
        ) : (
          <div
            onClick={canEditNotes ? onNotesEditStart : undefined}
            title={isRiff
              ? 'Riff cards keep their title here — editing this renames the card'
              : (canEditNotes ? 'Click to edit notes (same field as the Production page’s Notes)' : undefined)}
            style={{
              fontSize: 11, color: record.notes ? 'var(--tx-secondary)' : 'var(--tx-faint)',
              cursor: canEditNotes ? 'text' : 'default',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              padding: '2px 4px', border: '1px dashed transparent', borderRadius: 4,
            }}
            onMouseEnter={e => { if (canEditNotes) e.currentTarget.style.borderColor = 'var(--border)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
          >
            {isRiff && <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--violet)', marginRight: 5, textTransform: 'uppercase' }}>Title</span>}
            {record.notes || (canEditNotes ? '+ add' : '—')}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add Riff Card modal ───────────────────────────────────────────────────────

function RiffModal({ clients, members, onClose, onCreated }) {
  const [clientId,     setClientId]     = useState('');
  const [title,        setTitle]        = useState('');
  const [actorName,    setActorName]    = useState('');
  const [shootLeadId,  setShootLeadId]  = useState('');
  const [editorId,     setEditorId]     = useState('');
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  // T-3: the Producer select lists Producer-role members only (internal, active).
  const shootLeads = members.filter(m => m.isActive !== false && !m.isExternal && m.roles?.some(r => r.role === 'Producer'));
  const editors    = members.filter(m => m.roles?.some(r => r.role === 'Editor' || r.role === 'Power User'));

  async function handleCreate() {
    if (!clientId)      { setError('Project is required'); return; }
    if (!title.trim())  { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await api.post('/api/live-production', {
        clientId,
        actorName:   actorName   || null,
        shootLeadId: shootLeadId || null,
        editorId:    editorId    || null,
        notes:       title.trim(),
        status:      'Scripted',
      });
      // Fetch the created record with relations
      const detail = await api.get(`/api/live-production/${res.data.id}`);
      onCreated(detail.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create Riff card');
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-title">Add Riff Card</div>
        <div className="modal-sub">Create a production without a script — for riff or freestyle content</div>

        <div className="form-field">
          <label className="form-label">Project *</label>
          <select className="form-input" value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">Select a project…</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label">Title / Description *</label>
          <input
            className="form-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. DD-Animated Braces"
            autoFocus
          />
        </div>

        <div className="form-field">
          {/* Run 5 fold-in: label matches the grid's Talent column. The column
              underneath stays actor_name — this is a label, not a rename. */}
          <label className="form-label">Talent</label>
          <input
            className="form-input"
            value={actorName}
            onChange={e => setActorName(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="form-field">
          <label className="form-label">Producer</label>
          <select className="form-input" value={shootLeadId} onChange={e => setShootLeadId(e.target.value)}>
            <option value="">— Not assigned —</option>
            {shootLeads.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label">Editor</label>
          <select className="form-input" value={editorId} onChange={e => setEditorId(e.target.value)}>
            <option value="">— Not assigned —</option>
            {editors.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? 'Creating…' : 'Create Riff Card'}
          </button>
        </div>
      </div>
    </div>
  );
}
